import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { getChannel } from '../../../utils/channelNamesGridded';
import Tooltip from '../../../components/Tooltip';
import Switch from '../../../components/Switch';
import Slider from '../../../components/Slider';

interface FilterField {
  fieldName: string;
  displayName: string;
  min: number;
  max: number;
  step?: number;
  unit: string;
  conversionFactor?: number; // For unit conversion (e.g., km/h to m/s)
  defaultValue: number;
}

interface DataField {
  fieldName: string;
  displayName: string;
}

function applyFilter(values: number[], filters: number[], minimum: number, conversionFactor: number = 1): number[] {
  return values.filter((value: number, index: number) => {
    return filters[index] * conversionFactor >= minimum;
  });
}

function SpeedFilter(props: {
  rawData: any;
  setData: any;
  setFilterResults?: any;
  calibration: any;
  filterFields: FilterField[];
  dataFields: DataField[];
  sampleMask?: boolean[] | null; // Per-sample pre-filter (e.g. test-segment mask); applied before slider filters
  extraControls?: ReactNode; // Rendered to the right of the 'Use Default Filters' switch
}): ReactElement {
  const {
    rawData, setData, setFilterResults, calibration, filterFields, dataFields, sampleMask, extraControls,
  } = props;

  // Initialize state for each filter field
  const [filterValues, setFilterValues] = useState<Record<string, number>>(() => {
    const initialValues: Record<string, number> = {};
    filterFields.forEach((field) => {
      const storedValue = localStorage.getItem(`minimum${field.fieldName}`);
      initialValues[field.fieldName] = parseInt(storedValue || field.defaultValue.toString());
    });
    return initialValues;
  });

  const [totalLength, setTotalLength] = useState(0);
  const [filteredLengths, setFilteredLengths] = useState<Record<string, number>>({});
  const [useDefaultFilters, setUseDefaultFilters] = useState<boolean>(() => {
    return filterFields.every((field) => filterValues[field.fieldName] === field.defaultValue);
  });

  const handleFilterChange = (fieldName: string, event: Event, newValue: number | number[]): void => {
    const value: number = Array.isArray(newValue) ? newValue[0] : newValue;
    localStorage.setItem(`minimum${fieldName}`, value.toString());
    setFilterValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  useEffect(() => {
    if (!rawData || Object.keys(rawData).length === 0) return;

    // Optional per-sample pre-mask (e.g. restrict to test segments) applied before the slider filters
    const applyMask = (values: number[]): number[] =>
      sampleMask ? values.filter((value: number, index: number) => sampleMask[index]) : values;

    // Get all filter data
    const filterData: Record<string, number[]> = {};
    filterFields.forEach((field) => {
      filterData[field.fieldName] = applyMask(getChannel(rawData, field.fieldName, calibration) || []);
    });

    // Get all data fields
    const dataFieldValues: Record<string, number[]> = {};
    dataFields.forEach((field) => {
      dataFieldValues[field.fieldName] = applyMask(getChannel(rawData, field.fieldName, calibration) || []);
    });

    // Find the length of the first data field to use as total length
    const firstDataField = dataFields[0];
    const totalLength = dataFieldValues[firstDataField.fieldName]?.length || 0;
    setTotalLength(totalLength);

    // Apply filters sequentially
    const currentFilteredData: Record<string, number[]> = { ...dataFieldValues };
    let currentFilteredLength = totalLength;
    const newFilteredLengths: Record<string, number> = {};

    filterFields.forEach((filterField) => {
      const filterDataValues = filterData[filterField.fieldName];
      const conversionFactor = filterField.conversionFactor || 1;
      const currentFilterValue = filterValues[filterField.fieldName];

      // Apply this filter to all data fields
      Object.keys(currentFilteredData).forEach((dataFieldName) => {
        currentFilteredData[dataFieldName] = applyFilter(
          currentFilteredData[dataFieldName],
          filterDataValues,
          currentFilterValue,
          conversionFactor,
        );
      });

      // Update filtered length for this step
      const firstDataFieldName = Object.keys(currentFilteredData)[0];
      currentFilteredLength = currentFilteredData[firstDataFieldName]?.length || 0;
      newFilteredLengths[filterField.fieldName] = currentFilteredLength;
    });

    setFilteredLengths(newFilteredLengths);
    setData(currentFilteredData);
    if (setFilterResults) {
      setFilterResults({
        filterValues,
        totalLength,
        filteredLengths: newFilteredLengths,
      });
    }
  }, [rawData, calibration, filterValues, filterFields, dataFields, sampleMask]);

  function changeDefaultFilters(event: any, value: boolean) {
    if (value) {
      // Reset all filters to default values
      const newFilterValues: Record<string, number> = {};
      filterFields.forEach((field) => {
        newFilterValues[field.fieldName] = field.defaultValue;
        localStorage.setItem(`minimum${field.fieldName}`, field.defaultValue.toString());
      });
      setFilterValues(newFilterValues);
    }
    setUseDefaultFilters(value);
  }

  const getFilteredCountText = () => {
    if (filterFields.length === 0) return '';
    const lastFilterField = filterFields[filterFields.length - 1];
    const finalFilteredLength = filteredLengths[lastFilterField.fieldName] || 0;
    return `Showing ${finalFilteredLength} out of ${totalLength} total`;
  };

  return (
    <>
      <div className="cr-flex cr-items-center cr-py-5">
        <Switch checked={useDefaultFilters} onChange={changeDefaultFilters} aria-label="Use Default Filters" />
        <Tooltip
          title={
            <>
              {filterFields.map((field) => (
                <div key={field.fieldName}>
                  Default Minimum {field.displayName}: {field.defaultValue} {field.unit}
                </div>
              ))}
              {useDefaultFilters && <div>{getFilteredCountText()}</div>}
            </>
          }
          placement="top-start"
        >
          <p className="cr-ml-2 cr-hidden cr-text-sm md:cr-block">Use Default Filters </p>
        </Tooltip>
        {extraControls && <div className="cr-ml-6">{extraControls}</div>}
      </div>
      {!useDefaultFilters && (
        <div className="cr-flex cr-flex-col cr-items-center cr-gap-5">
          {filterFields.map((field, index) => {
            const previousFilterField = index > 0 ? filterFields[index - 1] : null;
            const previousFilteredLength = previousFilterField
              ? filteredLengths[previousFilterField.fieldName]
              : totalLength;
            const currentFilteredLength = filteredLengths[field.fieldName] || 0;

            return (
              <div key={field.fieldName} className="cr-w-full">
                <Tooltip
                  title={`Showing ${currentFilteredLength} out of ${previousFilteredLength} total`}
                  placement="top-start"
                >
                  <p className="cr-text-sm">
                    Minimum {field.displayName}: {filterValues[field.fieldName]} {field.unit}
                  </p>
                </Tooltip>
                <Slider
                  value={filterValues[field.fieldName]}
                  onChange={(event: any, newValue: number | number[]) =>
                    handleFilterChange(field.fieldName, event, newValue)
                  }
                  aria-label={`Minimum ${field.displayName}`}
                  min={field.min}
                  max={field.max}
                  step={field.step || 1}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default SpeedFilter;
